import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useFetchPostedPayments } from 'queries/premiums'

import { Alert } from '@cmsgov/design-system'
import { Card } from '@cmsgov/ds-medicare-gov'
import PostedPaymentCard from 'containers/Premiums/components/paymentHistory/PostedPaymentsCard/PostedPaymentCard'
import { SsaLink } from '../../components/SsaLink'
import { Button } from 'components/Button/Button'
import Skeleton from 'react-loading-skeleton'
import { MyLinkClickHandler } from 'components/MyLink'
import { useEvent } from 'hooks/useEvent'

export const PostedPayments = () => {
  const { t } = useTranslation('premiumsHistory')
  const { fireEvent } = useEvent('premiumsHistory')
  const { data, isError, isLoading, isSuccess } = useFetchPostedPayments()
  const payments = data ?? []
  const [displayPayments, setDisplayPayments] = useState(10)
  const totalPayments = payments.length
  const showLoadMoreLoadAll = (displayPayments < totalPayments) && !isError

  const onShowMoreSearchResults = () => {
    const remainingPayments = totalPayments - displayPayments
    const newDisplayPayments = displayPayments + (remainingPayments >= 10 ? 10 : remainingPayments)
    setDisplayPayments(newDisplayPayments)
    fireEvent('premiums_paymenthistory_loadmore_click')
  }

  const onShowAllSearchResults = () => {
    setDisplayPayments(totalPayments)
    fireEvent('premiums_paymenthistory_loadall_click')
  }

  const handleSsaLinkClick: MyLinkClickHandler = (params) => fireEvent('premiums_paymenthistory_visitssaccount_click', params)

  return (
    <div className="ds-l-container">
      <h2 className="ds-text-heading--lg">{t('postedPayments.heading')}</h2>
      {isError && (
        <Alert variation='error' heading={t('postedPayments.errorAlert.heading')} className='ds-u-measure--wide'>
          {t('postedPayments.errorAlert.content')}
        </Alert>
      )}

      {isSuccess && totalPayments === 0 && (
        <Alert heading={t('postedPayments.noRecordsAlert.heading')} className='ds-u-measure--wide'>
          <Trans t={t} i18nKey={'postedPayments.noRecordsAlert.content'} components={{
            link1: <SsaLink onClick={handleSsaLinkClick} />
          }} />
        </Alert>
      )}

      {!isError && (
        <>
          <div className="ds-l-row">
            <div className="ds-l-md-col--11 ds-l-sm-col--12">
              {!isLoading && totalPayments > 0 && (
                <Trans t={t} i18nKey={'postedPayments.content'} components={{
                  link1: <SsaLink onClick={handleSsaLinkClick} />
                }} />
              )}
            </div>
          </div>
          <div className="ds-l-row">
            <div className='ds-l-md-col--12 ds-l-sm-col--12 ds-u-margin-top--3'>
              {isLoading && <OnlinePaymentLoading />}

              {!isLoading && totalPayments > 0 && payments.slice(0, displayPayments).map(item => (
                <PostedPaymentCard
                  postedPayment={item}
                  className='ds-u-margin-bottom--2'
                />
              ))}
            </div>
          </div>
      {showLoadMoreLoadAll && isSuccess && (
        <div className='ds-l-row ds-u-margin-top--4'>
          <div className='ds-l-col--12'>
            <Button
              className='ds-c-button ds-c-button--solid ds-c-button--alternate ds-u-margin-right--2'
              onClick={onShowMoreSearchResults}
              >
                {t('postedPayments.loadMore')}
            </Button>
            <Button
              className='ds-c-button'
              onClick={onShowAllSearchResults}
              >
                {t('postedPayments.loadAll')}
            </Button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}

const OnlinePaymentLoading = () => {
  return (
    <Card className='ds-u-padding--3'>
      <div data-testid='online-payment-card ds-l-container'>
        <div className='ds-l-row'>
          <div className='ds-l-col--12 ds-l-md-col--6'>
            <Skeleton width={150} height={24} />
            <Skeleton width={220} height={40} className='ds-u-margin-bottom--3' />
            <Skeleton width={150} height={24} />
            <Skeleton width={220} height={40} />
          </div>

          <div className='ds-l-col--12 ds-l-md-col--6'>
            <Skeleton height={185} />
          </div>
        </div>
      </div>
    </Card>
  )
}
